import { PartialViewState } from "@nova/constants"
import EventEmitter from "events"
import { WebMercatorViewport } from "integrations/deckgl"
import { debounce } from "nodash"
import { getMapDimensions } from "./control/useMapControl"
import { MutableRefObject, useEffect, useMemo, useState } from "react"
import { MapRef } from "react-map-gl"
import { calculateTrigonmetricDistance } from "@nova/calculate-geospatial"
import { getGlobalTerrainData } from "./GlobalTerrainManager"
import { DiscriminatedUnion } from "@nova/util-ts"

const defaultEmptyViewstate = {
    longitude: -98.894405,
    latitude: 39.704277,
    zoom: 4,
    pitch: 0,
    bearing: 0,
}

const view: { viewState: PartialViewState; viewport: WebMercatorViewport } = {
    viewState: { ...defaultEmptyViewstate },
    viewport: new WebMercatorViewport(defaultEmptyViewstate),
}

export type ViewStateOptions = DiscriminatedUnion<
    ({ disableFly?: boolean; forceFly?: false } | { disableFly?: false; forceFly?: boolean }) & {
        minFlyDurationMs?: number
        maxFlyDurationMs?: number
    }
>

export const viewStateEventEmitter = new EventEmitter()

export const getGlobalMapRef = () =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- I couldn't find a better way to pass the map ref around
    (window as any).mapRef as MutableRefObject<MapRef | null> | undefined
const globalMapLoadedEmitter = new EventEmitter()
export const notifyGlobalMapLoaded = () => globalMapLoadedEmitter.emit("loaded")
export const useGlobalMap = () => {
    const [map, setMap] = useState(() => getGlobalMapRef()?.current?.getMap())
    useEffect(() => {
        const sync = () => setMap(getGlobalMapRef()?.current?.getMap())
        globalMapLoadedEmitter.on("loaded", sync)
        sync()
        return () => {
            globalMapLoadedEmitter.off("loaded", sync)
        }
    }, [])
    return map
}
export const setGlobalMapRef = (map: MutableRefObject<MapRef | null> | undefined) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(window as any).mapRef = map
}
export const getViewport = () => view.viewport
export const getViewportBounds = () => {
    const [minLng, minLat, maxLng, maxLat] = getViewport().getBounds()
    return { minLng, minLat, maxLng, maxLat }
}
export const getViewState = () => view.viewState
export const useViewport = () => {
    const [viewport, setViewport] = useState(view.viewport)
    useEffect(() => {
        const setNewViewport = (newViewport: WebMercatorViewport) => {
            setViewport(newViewport)
        }
        viewStateEventEmitter.on("updatedViewport", setNewViewport)
        return () => {
            viewStateEventEmitter.off("updatedViewport", setNewViewport)
        }
    }, [])
    return viewport
}
let lastLoggedZoom: number | undefined
export const useDebouncedViewport = (waitMs: number) => {
    const viewport = useViewport()
    const [debouncedViewport, setDebouncedViewport] = useState(viewport)
    const debouncedSetViewport = useMemo(() => debounce(setDebouncedViewport, waitMs), [waitMs])
    useEffect(() => {
        debouncedSetViewport(viewport)
    }, [viewport, debouncedSetViewport])
    useEffect(() => () => debouncedSetViewport.cancel(), [debouncedSetViewport])
    return debouncedViewport
}
export const updateViewport = (viewState: PartialViewState) => {
    if (!viewStateEventEmitter.listenerCount("updatedViewport")) return
    view.viewState = { ...viewState, ...getMapDimensions() }
    const newViewport = new WebMercatorViewport(view.viewState)
    view.viewport = newViewport
    if (view.viewState.zoom !== lastLoggedZoom) {
        lastLoggedZoom = view.viewState.zoom
    }
    viewStateEventEmitter.emit("updatedViewport", newViewport)
}
export const setNewViewstate = (
    newViewState: Partial<PartialViewState>,
    options?: ViewStateOptions
) => {
    const originalViewState = view.viewState
    const nextViewState = { ...view.viewState, ...newViewState }
    view.viewState = nextViewState
    viewStateEventEmitter.emit("writeViewState", view.viewState, options, originalViewState)
}
export const resetViewstate = () => {
    view.viewState = { ...defaultEmptyViewstate }
    view.viewport = new WebMercatorViewport(defaultEmptyViewstate)
}

function getFlyToDuration(
    current: PartialViewState,
    next: PartialViewState,
    forceFly = false,
    minDurationMs = 1000,
    maxDurationMs = 2000
) {
    const distance =
        calculateTrigonmetricDistance(
            { latitude: current.latitude, longitude: current.longitude },
            { latitude: next.latitude, longitude: next.longitude }
        ) / 1000
    if (!forceFly && distance > 100) return 0 // jump straight to the target if distance is bigger then 100km
    const zoomDelta = Math.abs(next.zoom - current.zoom)
    const distanceWithZoom = distance + zoomDelta * 10

    // Normalize to [0, 1]
    const normalizedDistance = Math.min(Math.max(distanceWithZoom / 100, 0), 1)
    return minDurationMs + normalizedDistance * (maxDurationMs - minDurationMs)
}

export const useMapViewStateWriter = (mapRef: MutableRefObject<MapRef | null>) => {
    useEffect(() => {
        const onNewViewState = (
            viewState: PartialViewState,
            options: ViewStateOptions = {},
            originalViewState: PartialViewState
        ) => {
            if (!mapRef.current) return
            const { disableFly, forceFly, minFlyDurationMs, maxFlyDurationMs } = options
            const flyDuration = getFlyToDuration(
                originalViewState,
                viewState,
                forceFly,
                minFlyDurationMs,
                maxFlyDurationMs
            )
            const params = {
                center: [viewState.longitude, viewState.latitude] as [number, number],
                zoom: viewState.zoom,
                bearing: viewState.bearing ?? 0,
                pitch: viewState.pitch ?? 0,
            }
            if (!getGlobalTerrainData().terrainState && !disableFly && flyDuration)
                mapRef.current.flyTo({ ...params, duration: flyDuration })
            else mapRef.current.jumpTo(params)
        }
        viewStateEventEmitter.on("writeViewState", onNewViewState)
        return () => {
            viewStateEventEmitter.off("writeViewState", onNewViewState)
        }
    }, [mapRef])
}
